import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  api, startMapping, stopMapping, getMapBuild, listMapBuilds,
  activateMapBuild, keepMapBuild, discardMapBuild, getMapCameraState,
  listCameraMaps, importCameraMap, syncMapToCamera,
  type MapBuild,
} from "../../api/rest";
import { useSiteId } from "../../hooks/useSiteId";
import { MappingScanView } from "../../scene/MappingScanView";
import { MapPreviewView } from "../../scene/MapPreviewView";
import { MapGridEditor } from "../MapGridEditor";
import css from "./MapCaptureTab.module.css";

const ACTIVE = new Set<MapBuild["status"]>(["mapping", "building"]);

/** Captura sub-tab of Mapa (spec 2026-08-11): the V2Pro SLAM walk that
 *  produces the map `MapAlignTab` then places. `WarehousePage` already
 *  validated `:warehouseId` and renders the shared title/back link; `MapTab`
 *  renders the Captura/Alinhamento bar above this outlet. */
export function MapCaptureTab() {
  const { t, i18n } = useTranslation();
  const siteId = useSiteId();
  // Warehouse-anchored since ADR 145: entered from the warehouse row (like
  // Estrutura/Calibração) — the map/model this page produces belongs to that
  // warehouse, not to a global context selection.
  const { warehouseId = "" } = useParams();
  const [assetId, setAssetId] = useState("");
  const [name, setName] = useState("");
  const [buildId, setBuildId] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);

  // Resume the warehouse's latest build on page load — a refresh (or a later visit)
  // otherwise loses the preview/adjust/download context entirely.
  const history = useQuery({
    queryKey: ["mapBuilds", siteId, warehouseId],
    queryFn: () => listMapBuilds(siteId, warehouseId!),
    enabled: !!siteId && !!warehouseId && !buildId,
  });
  // Guarded adjust-during-render (react.dev "adjusting state when a prop changes"):
  // self-limiting — setting buildId makes the guard false on the re-render.
  if (!buildId && history.data?.length) {
    const b = history.data.find(
      (x) => x.status !== "discarded" && x.status !== "failed" && x.status !== "cancelled");
    if (b) {
      setBuildId(b.id);
      setAssetId(b.mappingAssetId);
    }
  }

  const { data: assets = [] } = useQuery({
    queryKey: ["assets", siteId, warehouseId],
    queryFn: () => api.listAssets(siteId, warehouseId),
    enabled: !!siteId,
  });
  // Mapping needs a live command path: an approved bound edge for MQTT assets, a
  // dialable URL for HTTP ones. A pasted URL alone doesn't make an MQTT asset capable.
  const capable = assets.filter((a) =>
    a.commandTransport === "mqtt" ? a.edgeBound : !!a.gatewayBaseUrl);

  const build = useQuery({
    queryKey: ["mapBuild", buildId],
    queryFn: () => getMapBuild(buildId!),
    enabled: !!buildId,
    // Poll faster while the camera is generating so the progress bar is smooth.
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      if (s === "building") return 1000;
      if (s === "mapping") return 2000;
      return false;
    },
  });
  const status = build.data?.status;

  // Camera-map manager (ADR 128): the maps already on the equipment, importable without a walk.
  const cameraMaps = useQuery({
    queryKey: ["cameraMaps", assetId],
    queryFn: () => listCameraMaps(assetId),
    enabled: !!assetId && status !== "mapping" && status !== "building",
    refetchOnWindowFocus: false,
  });

  // Truthful camera state while previewing (V01 active map / V02 localized).
  const cameraState = useQuery({
    queryKey: ["mapCameraState", buildId],
    queryFn: () => getMapCameraState(buildId!),
    enabled: !!buildId && (status === "ready" || status === "active"),
    refetchInterval: status === "ready" || status === "active" ? 3000 : false,
  });

  const start = useMutation({
    mutationFn: () => startMapping(assetId, name.trim()),
    onSuccess: (b) => setBuildId(b.id),
  });
  const stop = useMutation({
    mutationFn: () => stopMapping(buildId!),
    onSuccess: () => build.refetch(),
  });
  const activate = useMutation({
    mutationFn: () => activateMapBuild(buildId!),
    onSuccess: () => build.refetch(),
  });
  const keep = useMutation({
    mutationFn: () => keepMapBuild(buildId!),
    onSuccess: () => build.refetch(),
  });
  const discard = useMutation({
    mutationFn: () => discardMapBuild(buildId!),
    onSuccess: () => build.refetch(),
  });
  const importMap = useMutation({
    mutationFn: (name: string) => importCameraMap(assetId, name),
    onSuccess: (b) => setBuildId(b.id),
  });
  // Slice 2 (ADR 129): push this map to the camera's localization (restarts the equipment).
  const syncCam = useMutation({ mutationFn: () => syncMapToCamera(buildId!) });
  const deciding = activate.isPending || keep.isPending || discard.isPending;

  const running = status ? ACTIVE.has(status) : false;
  const statusClass = status === "ready" || status === "active" ? css.ok
    : status === "failed" ? css.bad : css.active;
  // bug05: bridge the gap between "stop" and the server flipping to "building".
  const statusText = stop.isPending && status === "mapping"
    ? t("mapping.finishing")
    : status ? t(`mapping.status.${status}`) : "…";

  return (
    <div className={css.root}>
      {!warehouseId && <div className={css.warn}>{t("mapping.selectWarehouse")}</div>}

      <div className={css.form}>
        <div className={css.field}>
          <label className={css.label} htmlFor="map-asset">{t("mapping.asset")}</label>
          <select id="map-asset" className={css.input} value={assetId}
                  onChange={(e) => setAssetId(e.target.value)} disabled={running}>
            <option value="">{t("mapping.selectPlaceholder")}</option>
            {capable.map((a) => <option key={a.id} value={a.id}>{a.label} ({a.id})</option>)}
          </select>
          {capable.length === 0 && (
            <div className={css.hint}>{t("mapping.noCapable")}</div>
          )}
        </div>
        <div className={css.field}>
          <label className={css.label} htmlFor="map-name">{t("mapping.mapName")}</label>
          <input id="map-name" className={css.input} value={name}
                 onChange={(e) => setName(e.target.value)} placeholder={t("mapping.mapNamePlaceholder")} disabled={running} />
        </div>
        <button className={css.btn}
                disabled={!assetId || !name.trim() || !warehouseId || running || start.isPending}
                onClick={() => start.mutate()}>
          {start.isPending ? t("mapping.starting") : t("mapping.start")}
        </button>
      </div>

      {start.isError && <div className={css.err}>{t("mapping.startError", { message: (start.error as Error).message })}</div>}

      {/* Camera-map manager (ADR 128): import a map already on the equipment through the same
          preview/fix/activate rail — no walk. Hidden while a run/build is active. */}
      {assetId && status !== "mapping" && status !== "building" && (
        <div className={css.statusCard}>
          <div className={css.statusRow}>
            <span className={css.statusLabel}>{t("mapping.cameraMaps.title")}</span>
            <button type="button" className={css.pkgLink} disabled={cameraMaps.isFetching}
                    onClick={() => cameraMaps.refetch()}>
              {cameraMaps.isFetching ? t("mapping.cameraMaps.querying") : t("mapping.cameraMaps.refresh")}
            </button>
          </div>
          {cameraMaps.data && !cameraMaps.data.available && (
            <div className={css.hint}>{t("mapping.cameraMaps.unavailable")}</div>
          )}
          {cameraMaps.data?.available && cameraMaps.data.maps.length === 0 && (
            <div className={css.hint}>{t("mapping.cameraMaps.empty")}</div>
          )}
          {cameraMaps.data?.available && cameraMaps.data.maps.map((m) => (
            <div key={m.name} className={css.statusRow}>
              <span className={css.statusValue}>
                {m.name}
                {m.active && <span className={css.editedTag}> {t("mapping.cameraMaps.active")}</span>}
                {/* Saved ("background") maps aren't runnable on the camera yet, but their zip
                    is fetchable, so the import rail works the same (ADR 141). */}
                {m.deployed === false && <span className={css.hint}> {t("mapping.cameraMaps.notDeployed")}</span>}
              </span>
              <button type="button" className={css.btn} disabled={importMap.isPending}
                      onClick={() => importMap.mutate(m.name)}>
                {importMap.isPending ? t("mapping.cameraMaps.importing") : t("mapping.cameraMaps.import")}
              </button>
            </div>
          ))}
          {importMap.isError && (
            <div className={css.err}>{t("mapping.cameraMaps.importError", { message: (importMap.error as Error).message })}</div>
          )}
        </div>
      )}

      {buildId && (
        <div className={css.statusCard}>
          <div className={css.statusRow}>
            <span className={css.statusLabel}>{t("common.col.status")}</span>
            <span className={`${css.statusValue} ${statusClass}`}>{statusText}</span>
          </div>

          {status === "mapping" && assetId && (
            <MappingScanView buildId={buildId!} />
          )}
          {build.data?.error && <div className={css.err}>{build.data.error}</div>}
          {running && (
            <button className={css.stopBtn} disabled={stop.isPending || status !== "mapping"}
                    onClick={() => stop.mutate()}>
              {stop.isPending ? t("mapping.stopping") : t("mapping.stop")}
            </button>
          )}
          {stop.isError && (
            <div className={css.err}>{t("mapping.stopError", { message: (stop.error as Error).message })}</div>
          )}
          {status === "building" && (
            <div className={css.statusRow}>
              <span className={css.statusLabel}>{t("mapping.generating")}</span>
              <span className={css.statusValue}>
                {build.data?.buildProgressPct != null
                  ? `${Math.round(build.data.buildProgressPct)}%`
                  : "…"}
              </span>
            </div>
          )}

          {(status === "ready" || status === "active") && build.data?.areaM2 != null && (
            <div className={css.statusRow}>
              <span className={css.statusLabel}>{t("mapping.areaMapped")}</span>
              <span className={css.statusValue}>
                {build.data.areaM2.toLocaleString(i18n.language, { maximumFractionDigits: 1 })} m²
              </span>
            </div>
          )}

          {/* Preview → keep/discard (decisions.md#77): the build is a preview only; the operator decides. */}
          {status === "ready" && (
            <>
              <div className={css.previewHint}>{t("mapping.preview.hint")}</div>
              {build.data?.modelUrl && <MapPreviewView modelUrl={build.data.modelUrl} />}
              <CameraChecks state={cameraState.data} />
              {(activate.isError || keep.isError || discard.isError) && (
                <div className={css.err}>
                  {((activate.error || keep.error || discard.error) as Error)?.message}
                </div>
              )}
              <div className={css.previewActions}>
                <button className={css.activateBtn} disabled={deciding} onClick={() => activate.mutate()}>
                  {activate.isPending ? t("mapping.preview.activating") : t("mapping.preview.activate")}
                </button>
                <button className={css.keepBtn} disabled={deciding} onClick={() => keep.mutate()}>
                  {keep.isPending ? t("mapping.preview.keeping") : t("mapping.preview.keep")}
                </button>
                <button className={css.discardBtn} disabled={deciding} onClick={() => discard.mutate()}>
                  {discard.isPending ? t("mapping.preview.discarding") : t("mapping.preview.discard")}
                </button>
              </div>
              {/* Slice 2 (ADR 129): push the map back to the camera's localization. */}
              <button type="button" className={css.keepBtn} disabled={syncCam.isPending}
                      onClick={() => { if (window.confirm(t("mapping.sync.confirm"))) syncCam.mutate(); }}>
                {syncCam.isPending ? t("mapping.sync.syncing") : t("mapping.sync.button")}
              </button>
              {syncCam.data && (
                <div className={syncCam.data.ok ? css.hint : css.err}>
                  {syncCam.data.ok ? t("mapping.sync.ok")
                    : syncCam.data.rolledBack ? t("mapping.sync.rolledBack")
                    : t("mapping.sync.failed", { message: syncCam.data.error ?? "" })}
                </div>
              )}
              {syncCam.isError && (
                // A transport timeout/5xx here doesn't mean the sync failed — the camera restart +
                // re-localize can outlast the request. Say so instead of claiming failure (ADR 134).
                <div className={css.hint}>{t("mapping.sync.unconfirmed")}</div>
              )}
            </>
          )}

          {status === "active" && (
            <>
              <div className={`${css.banner} ${css.bannerOk}`}>{t("mapping.preview.activated")}</div>
              <CameraChecks state={cameraState.data} />
              <Link className={css.viewBtn} to="/live">{t("mapping.viewModel")}</Link>
            </>
          )}
          {status === "kept" && (
            <div className={`${css.banner} ${css.bannerMuted}`}>{t("mapping.preview.kept")}</div>
          )}
          {status === "discarded" && (
            <div className={`${css.banner} ${css.bannerMuted}`}>{t("mapping.preview.discarded")}</div>
          )}

          {/* Raw-package download (designer handoff) + 2D cleanup (ADR 102). */}
          {(status === "ready" || status === "active" || status === "kept")
            && build.data?.packageUrl && (
            <>
              <div className={css.packageRow}>
                <a className={css.pkgLink} href={build.data.packageUrl} download>
                  {t("mapping.package.download")}
                </a>
                <button type="button" className={css.editGridBtn}
                        aria-expanded={editorOpen}
                        onClick={() => setEditorOpen((o) => !o)}>
                  {t("mapping.grid.edit")}
                </button>
                {build.data.gridEdited && (
                  <span className={css.editedTag}>{t("mapping.grid.editedBadge")}</span>
                )}
              </div>
              {editorOpen && (
                <MapGridEditor
                  buildId={buildId!}
                  onRebuildStarted={() => {
                    setEditorOpen(false);
                    void build.refetch();
                  }}
                />
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// V01 (active map) / V02 (localized) truthful chips. Hidden when the edge can't be queried
// synchronously (no-inbound mqtt edge), where the camera state is simply unavailable.
function CameraChecks({ state }: { state?: import("../../api/rest").MapCameraState }) {
  const { t } = useTranslation();
  if (!state || !state.available) return null;
  return (
    <div className={css.checks}>
      <span className={`${css.chip} ${state.isActive ? css.chipOk : css.chipBad}`}>
        {state.isActive ? t("mapping.preview.v01active") : t("mapping.preview.v01inactive")}
      </span>
      <span className={`${css.chip} ${state.located ? css.chipOk : css.chipBad}`}>
        {state.located ? t("mapping.preview.v02located") : t("mapping.preview.v02notlocated")}
      </span>
    </div>
  );
}
